import axios from 'axios';
import axiosInstance from '../axiosInstance';
import { getManagerRoutePath } from '../managerRouteUtil';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * Staff Service
 * Handles API calls for staff management
 * Routes dynamically based on role_id from localStorage user.role.id
 */

/**
 * Get all staff members with pagination
 */
export const getAllStaff = async (page = 1, limit = 10, search = '', roleId = '', status = '', archived = false) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.get(`${API_BASE_URL}/${routePath}/staff`, {
      params: { page, limit, search, roleId, status, archived },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error fetching staff' };
  }
};

/**
 * Set a staff member's Active / Inactive status.
 */
export const setStaffStatus = async (staffId, isActive) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.patch(`${API_BASE_URL}/${routePath}/staff/${staffId}/status`, { is_active: isActive });
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error updating status' };
  }
};

/**
 * Mark a staff member as having left the organisation, or reverse it.
 * A leaver stays in the list under a Leaver status (unlike archiving, which
 * hides them) but loses sign-in access.
 */
export const setStaffLeaver = async (staffId, isLeaver, { leftOn, reason } = {}) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.patch(
      `${API_BASE_URL}/${routePath}/staff/${staffId}/leaver`,
      { is_leaver: isLeaver, left_on: leftOn, reason },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error updating leaver status' };
  }
};

/**
 * Archive / restore a staff member.
 */
export const archiveStaff = async (staffId, archived) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.patch(`${API_BASE_URL}/${routePath}/staff/${staffId}/archive`, { archived });
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error archiving staff' };
  }
};

/**
 * Multi-site groups: move a staff member to another site of the group.
 * Account owner only (the API enforces it).
 */
export const moveStaffToSite = async (staffId, siteId) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`${API_BASE_URL}/${routePath}/staff/${staffId}/move-site`, { site_id: siteId });
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error moving staff to another site' };
  }
};

/**
 * Email a password/PIN reset link to a staff member.
 */
export const resetStaffPassword = async (staffId) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`${API_BASE_URL}/${routePath}/staff/${staffId}/reset-password`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error sending reset email' };
  }
};

/**
 * Get single staff member by ID
 */
export const getStaffById = async (staffId) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.get(`${API_BASE_URL}/${routePath}/staff/${staffId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error fetching staff' };
  }
};

/**
 * Create new staff member
 */
export const createStaff = async (staffData) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`${API_BASE_URL}/${routePath}/staff`, staffData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error creating staff' };
  }
};

/**
 * Update staff member
 */
export const updateStaff = async (staffId, staffData) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.put(
      `${API_BASE_URL}/${routePath}/staff/${staffId}`,
      staffData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error updating staff' };
  }
};

/**
 * Delete staff member
 */
export const deleteStaff = async (staffId) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.delete(`${API_BASE_URL}/${routePath}/staff/${staffId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error deleting staff' };
  }
};

/**
 * Get shift groups for staff roster
 */
/**
 * Is an email address free to use? Advisory only — the save still enforces it.
 * `excludeId` keeps somebody's own address available while editing them.
 */
export const checkEmailAvailable = async (email, excludeId = null) => {
  const routePath = getManagerRoutePath();
  const params = new URLSearchParams({ email });
  if (excludeId) params.set('exclude_id', String(excludeId));
  const response = await axiosInstance.get(
    `${API_BASE_URL}/${routePath}/staff/email-available?${params.toString()}`,
  );
  return response.data;
};

export const getStaffGroups = async () => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.get(`${API_BASE_URL}/${routePath}/staff/groups`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error fetching groups' };
  }
};

/**
 * Create a new employee group
 */
export const createStaffGroup = async (groupData) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`${API_BASE_URL}/${routePath}/staff/groups`, groupData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error creating group' };
  }
};

/**
 * Get roster for specific staff
 */
export const getStaffRoster = async (staffId) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.get(`${API_BASE_URL}/${routePath}/staff/${staffId}/roster`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error fetching roster' };
  }
};

/**
 * Create or update staff roster
 */
export const upsertStaffRoster = async (staffId, rosterData) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`${API_BASE_URL}/${routePath}/staff/${staffId}/roster`, rosterData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error saving roster' };
  }
};

/**
 * Approve staff member
 */
export const approveStaff = async (staffId) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`${API_BASE_URL}/${routePath}/staff/${staffId}/approve`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error approving staff' };
  }
};

/**
 * Reject staff member
 */
export const rejectStaff = async (staffId) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`${API_BASE_URL}/${routePath}/staff/${staffId}/reject`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error rejecting staff' };
  }
};

/**
 * Attach a document to a staff member.
 *
 * `file` is a browser File. On a brand-new staff member there is no id to post
 * to yet, so the Add Staff form holds the picked files and calls this once the
 * record has been created.
 *
 * @param {number|string} staffId
 * @param {{ file: File, category?: string, name?: string, expiry?: string }} document
 */
export const addStaffDocument = async (staffId, { file, category, name, expiry }) => {
  try {
    const routePath = getManagerRoutePath();
    const body = new FormData();
    body.append('file', file);
    if (category) body.append('category', category);
    if (name) body.append('name', name);
    if (expiry) body.append('expiry', expiry);

    const response = await axiosInstance.post(
      `${API_BASE_URL}/${routePath}/staff/${staffId}/documents`,
      body,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          // The instance defaults to application/json. Undefined here makes
          // axios drop the header so the browser sets multipart/form-data with
          // its own boundary — without it the upload arrives unparseable.
          'Content-Type': undefined,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error adding document' };
  }
};

/**
 * Remove one of a staff member's documents.
 */
export const deleteStaffDocument = async (staffId, documentId) => {
  try {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.delete(
      `${API_BASE_URL}/${routePath}/staff/${staffId}/documents/${documentId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Error removing document' };
  }
};

export default {
  getAllStaff,
  getStaffById,
  createStaff,
  addStaffDocument,
  deleteStaffDocument,
  updateStaff,
  deleteStaff,
  getStaffGroups,
  createStaffGroup,
  checkEmailAvailable,
  getStaffRoster,
  upsertStaffRoster,
  approveStaff,
  rejectStaff,
  setStaffStatus,
  setStaffLeaver,
  archiveStaff,
  moveStaffToSite,
  resetStaffPassword,
};
